import { useQuery, type QueryKey } from '@tanstack/react-query';
import service from '../service';

export const flowDebugKeys = {
  logs: (params: { flowId?: string; fishId?: string }) =>
    ['flow-debug-logs', params] as const,
  instances: (params: { flowId?: string; fishId?: string }) =>
    ['flow-debug-instances', params] as const,
};

export interface FlowTraceEntry {
  ts: string;
  outcome: string;
  event?: string;
  flowStageType?: string;
  flowId?: string | null;
  flowStageId?: string | null;
  flowInstanceId?: string | null;
  fishId?: string | null;
  detail?: Record<string, unknown>;
}

// Polls the dev-only flow trace log for entries matching this flow + test fish.
// Refetches while the slideout is open so emitted events surface within seconds.
export function useFlowDebugLogs({
  flowId,
  fishId,
  enabled = true,
  refetchInterval = 2500,
}: {
  flowId?: string;
  fishId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  return useQuery<{ data: FlowTraceEntry[]; meta: { count: number } }>({
    queryKey: flowDebugKeys.logs({ flowId, fishId }) as unknown as QueryKey,
    queryFn: () => service.getFlowDebugLogs({ flowId, fishId, limit: 300 }),
    enabled: enabled && !!fishId,
    refetchInterval,
  });
}

// Polls flow instances scoped to this flow + test fish so stage results and
// trigger receipts appear live as the fish moves through the flow.
export function useFlowDebugInstances({
  flowId,
  fishId,
  enabled = true,
  refetchInterval = 2500,
}: {
  flowId?: string;
  fishId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  return useQuery({
    queryKey: flowDebugKeys.instances({ flowId, fishId }) as unknown as QueryKey,
    queryFn: () =>
      service.getFlowInstances(
        { include: 'flowStageResults' },
        flowId,
        undefined,
        undefined,
        fishId,
      ),
    enabled: enabled && !!fishId && !!flowId,
    refetchInterval,
  });
}
